import { FormProvider, useForm } from "react-hook-form";
import styles from "./styles.module.scss";
import utilStyles from "../utilStyles.module.scss";
import FormInput from "../../../components/Programs/FormInput";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RootState } from "typedefs";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, lowerFirst } from "lodash";
import { DateTime } from "luxon";
import Label from "../../../components/Programs/Label";
import ToolTip from "../../../components/Programs/Tooltip";
import StateDayPicker from "../../../components/Programs/StateDayPicker";
import SaveBar from "../../Sessions/SaveBar";
import SelectBox from "../../../components/OptionButtonGroup/SelectBox";
import { hoursWithLablesFromSpecificValue } from "../../../utils/timeUtils";
import { display_clinic_buttons, repeat_options } from "../Pricing/utils";
import {
  resetProgramSettings,
  setGeneralSettings,
} from "../../../actions/scheduling/program";
import { ProgramState } from "../../../reducers/scheduling/program";
import { useHistory } from "react-router-dom";
import ButtonsContainer from "app/components/Programs/Buttons/ButtonsContainer";
import InformationModal from "app/components/Programs/InformationModal";
import CalendarX from "../../../../public/images/calendar-x-red.svg";
import CustomRecurrenceModal from "app/components/Programs/CustomRecurrenceModal";
import { useTranslation } from "react-i18next";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";

const GeneralProgramOptions = () => {
  const { t } = useTranslation();
  const currentFacility = useCurrentFacility();
  const { preferences } = useCalendarPreferences();
  const timeFormat = preferences.timeFormat;
  const schedulingInterval = currentFacility?.data?.featureFlags?.schedulingInterval || 15;

  const privacy_options = [
    {
      value: "private",
      label: t("Private"),
    },
    {
      value: "public",
      label: t("Public"),
    },
  ];

  const restriction_options = [
    {
      value: true,
      label: t("No"),
    },
    {
      value: false,
      label: t("Yes"),
    },
  ];

  const generalOptionState = useSelector(
    (state: RootState): ProgramState => state.program
  );

  const methods = useForm({
    defaultValues: {
      category: generalOptionState.category,
      title: generalOptionState.title,
      subtitle: generalOptionState.subtitle,
      location: generalOptionState.location,
      email: generalOptionState.email,
      phone: generalOptionState.phone,
    },
  });
  const history = useHistory();
  const dispatch = useDispatch();
  const [date, setDate] = useState(
    generalOptionState.edit ?
      generalOptionState?.events[0].startTime :
      DateTime.now());
  const [startTime, setStartTime] = useState(generalOptionState.startTime);
  const [endTime, setEndTime] = useState(generalOptionState.endTime);
  const [untilDateError, setUntilDateError] = useState(false);
  const privacyOption = privacy_options.find(
    (option) => option.value === generalOptionState.privacy
  );
  const restrictionOption = restriction_options.find(
    (option) => option.value === generalOptionState.restrictAdditionalPeople
  );
  const [privacy, setPrivacy] = useState(privacyOption || privacy_options[0]);
  const [restriction, setRestriction] = useState(restrictionOption || restriction_options[1]);

  const [maxDate, setMaxDate] = useState<number | Date>(
    new Date().setFullYear(new Date().getFullYear() + 1)
  );
  const [repeat, setRepeat] = useState(() => {
    const savedRepeat = generalOptionState?.repeat;

    if (savedRepeat?.value === null || !savedRepeat) {
      return !generalOptionState.edit ? null : repeat_options()[0];
    }

    return savedRepeat;
  });

  const [untilDate, setUntilDate] = useState<string | Date | null>(
    generalOptionState.untilDate || null
  );

  const [selectedDisplayButton, setSelectedDisplayButton] = useState(display_clinic_buttons()[0]);

  const [isCustomRecurrenceModalOpen, setIsCustomRecurrenceModalOpen] = useState(false);
  const [customRecurrenceConfig, setCustomRecurrenceConfig] = useState(null);

  const [formErrors, setFormErrors] = useState({
    date: false,
    startTime: false,
    endTime: false,
    repeat: false,
    untilDate: false,
    phone: false,
  });

  const handleDateChange = useCallback(
    (value, label) => {
      if (label === "date") {
        const oneYearLater = DateTime.fromISO(value)
          .plus({ years: 1 })
          .toJSDate();
        const oneMonthLater = DateTime.fromISO(value)
          .plus({ months: 1 })
          .toJSDate();

        setDate(value);
        if (!generalOptionState.edit && repeat && repeat.value !== "Never") {
          setUntilDate(oneMonthLater.toISOString());
        }
        setMaxDate(repeat?.value === "daily" ? oneMonthLater : oneYearLater);
      } else {
        setUntilDate(value);
      }
    },
    [repeat]
  );

  const handleRepeat = (value) => {
    if (value.value === "custom") {
      setRepeat(value);
      setIsCustomRecurrenceModalOpen(true);
      if (formErrors.repeat) {
        setFormErrors((prevErrors) => ({ ...prevErrors, repeat: false }));
      }
      return;
    }

    if (value.value === "Never") {
      setUntilDate(null);
    } else if ((!repeat || repeat.value === "Never") && untilDate === null && date) {
      // Set default untilDate when switching from null/"Never" to a repeat option
      const oneMonthLater = DateTime.fromISO(date)
        .plus({ months: 1 })
        .toJSDate();
      setUntilDate(oneMonthLater.toISOString());
    }

    if (date) {
      const oneYearLater = DateTime.fromISO(date).plus({ years: 1 }).toJSDate();
      const oneMonthLater = DateTime.fromISO(date)
        .plus({ months: 1 })
        .toJSDate();
      if (value.value === "daily") {
        if (untilDate > oneMonthLater.toISOString()) {
          setUntilDate(oneMonthLater);
        }
        setMaxDate(oneMonthLater);
      } else {
        setMaxDate(oneYearLater);
      }
    }
    if (formErrors.repeat) {
      setFormErrors((prevErrors) => ({ ...prevErrors, repeat: false }));
    }
    setRepeat(value);
  };

  const buildCustomRecurrenceString = (config) => {
    if (!config) {
      return '';
    }

    // Start date prefix ("Starts Monday, July 13, 2026")
    const startDateValue = config.startDate
      ? DateTime.fromJSDate(config.startDate)
      : date
        ? DateTime.fromISO(date.toString()).toLocal()
        : null;
    const startPrefix = startDateValue
      ? `${t('Starts')} ${startDateValue.toFormat('cccc, MMMM d, yyyy')} · `
      : '';

    // Interval + frequency
    const interval = config.interval === 1
      ? config.frequency.slice(0, -1) // "day" or "week"
      : `${config.interval} ${config.frequency}`;

    // Days of week (only for weeks)
    const daysLabels = [t('Sun'), t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat')];
    const days = config.frequency === 'weeks' && config.daysOfWeek?.length > 0
      ? ` ${t('on')} ${config.daysOfWeek.map(d => daysLabels[d]).join(', ')}`
      : '';

    // End condition
    const end = config.untilDate
      ? ` ${t('until')} ${DateTime.fromJSDate(config.untilDate).toFormat('MMM d, yyyy')}`
      : ` ${t('for')} ${config.occurrences} ${t('sessions')}`;

    return `${startPrefix}${t('Every')} ${interval}${days}${end}`;
  };

  const handleSaveCustomRecurrence = (config: {
    interval: number;
    frequency: "days" | "weeks";
    daysOfWeek: number[];
    startDate?: Date | null;
    untilDate?: Date | null;
    occurrences?: number | null;
  }) => {
    setCustomRecurrenceConfig(config);
    if (config.startDate) {
      // Store an offset-less local ISO timestamp (YYYY-MM-DDTHH:mm:ss). Both
      // `new Date(...)` (the date picker) and `DateTime.fromISO(...)`
      // (constructDate) read it as local time, so the calendar day is stable
      // in every zone. A `Z` timestamp shifts the day for positive-offset
      // users; a date-only string is parsed as UTC by `new Date`, shifting it
      // for negative-offset users.
      setDate(DateTime.fromJSDate(config.startDate).toISO({ includeOffset: false }));
    }
    // Keep untilDate in sync with the chosen end condition. When the custom
    // recurrence is occurrences-based, clear any stale until date so Pricing
    // doesn't send a conflicting event.until_date. Use the same offset-less
    // local ISO format as the start date so the end day is stable in every zone
    // for both `new Date(...)` and `DateTime.fromISO(...)` consumers.
    setUntilDate(config.untilDate ? DateTime.fromJSDate(config.untilDate).toISO({ includeOffset: false }) : null);
  };

  const onChange = (time, label) => {
    if (label === "startTime") {
      setStartTime(time);
    } else {
      setEndTime(time);
    }
  };

  // QA on coaching_service#889 created a daily repeating *clinic* ending the day before
  // it started and was let straight through to the Details tab. Two reasons:
  //
  // - the check was gated on type === "Series", but a clinic repeats too, and whether
  //   the recurrence ends before it starts has nothing to do with the program type;
  // - untilDate is held as an ISO string on one path (the default a month out) and as a
  //   Date on another (the daily clamp), and `Date < string` coerces to NaN, so the
  //   comparison silently never fired on the second path even for a Series.
  //
  // custom recurrence stays out: it may carry occurrences instead of an until date, and
  // the API bounds that case on its own.
  //
  // Both dates go through toDateTime because neither has a single type: `date` starts as a
  // Luxon DateTime on the create path and becomes an ISO string once picked, `untilDate` is
  // an ISO string on one path and a Date on the daily clamp.
  const toDateTime = (value: unknown) => {
    if (DateTime.isDateTime(value)) {
      return value;
    }
    if (value instanceof Date) {
      return DateTime.fromJSDate(value);
    }
    return DateTime.fromISO(String(value));
  };

  const checkDateErrors = () => {
    if (!repeat || repeat.value === "Never" || repeat.value === "custom") {
      return false;
    }
    if (!untilDate || !date) {
      return false;
    }

    const until = toDateTime(untilDate);
    const start = toDateTime(date);
    if (!until.isValid || !start.isValid) {
      return false;
    }

    if (until.startOf("day") < start.startOf("day")) {
      setUntilDateError(true);
      return true;
    }
    return false;
  };

  const checkErrors = useCallback(() => {
    let hasErrors = false;
    const newErrors = { date: false, startTime: false, endTime: false, repeat: false };
    if (date === null) {
      newErrors.date = true;
      hasErrors = true;
    }

    if (startTime === null) {
      newErrors.startTime = true;
      hasErrors = true;
    }
    if (endTime === null) {
      newErrors.endTime = true;
      hasErrors = true;
    }

    if (!repeat) {
      newErrors.repeat = true;
      hasErrors = true;
    }

    setFormErrors((prevErrors) => ({ ...prevErrors, ...newErrors }));

    return hasErrors;
  }, [date, endTime, startTime, repeat]);

  useEffect(() => {
    if (!isEmpty(methods.formState.errors)) {
      checkErrors();
    }
  }, [methods.formState.errors, checkErrors]);

  useEffect(() => {
    if (!generalOptionState.edit && !date) {
      setDate(DateTime.local().toISODate());
    }
  }, [generalOptionState.edit, date]);

  const onSubmit = (data) => {
    const externalErrors = checkErrors();
    if (externalErrors) {
      return;
    }

    const dateErrors = checkDateErrors();
    if (dateErrors) {
      return;
    }

    const generalSettings = {
      category: data.category,
      title: data.title,
      subtitle: data.subtitle,
      date,
      startTime,
      endTime,
      location: data.location,
      email: data.email,
      privacy: privacy?.value,
      restrictAdditionalPeople: restriction?.value,
      untilDate: !repeat || repeat.value === "Never" ? null : untilDate,
      repeat,
      phone: data.phone,
      page: 2,
      bulkCreate: selectedDisplayButton?.value === 1,
      ...(customRecurrenceConfig && repeat?.value === "custom" ? {
        customRecurrence: {
          interval: customRecurrenceConfig.interval,
          frequency: customRecurrenceConfig.frequency,
          daysOfWeek: customRecurrenceConfig.daysOfWeek,
          ...(customRecurrenceConfig.untilDate ? { untilDate: customRecurrenceConfig.untilDate } : {}),
          ...(customRecurrenceConfig.occurrences ? { occurrences: customRecurrenceConfig.occurrences } : {}),
        }
      } : {}),
    };
    dispatch(setGeneralSettings(generalSettings));
  };

  const calendar = useMemo(() => {
    return (
      <StateDayPicker
        onChange={(value) => handleDateChange(value, "date")}
        date={date ? new Date(date) : undefined}
        openToDate={!date ? new Date() : undefined}
      />
    );
  }, [date, handleDateChange]);

  return (
    <div
      className={`${styles.container} programs-page-container`}
    >
      <FormProvider {...methods}>
        <form id="programForm">
          <div className={styles.row}>
            <div className={`${utilStyles.column} ${styles.section}`}>
              <label>{t("General")}</label>
              <div className={styles.horizontal_line} />
              <div className={styles.column_gap_16}>
                <FormInput
                  tooltipText={t(
                    "Organize your programs into categories for custom reporting."
                  )}
                  tagTitle={t("Program Category")}
                  placeholder={t("Ex: Junior Programs")}
                  fieldName="category"
                  isRequired={true}
                />
                <FormInput
                  tagTitle={t("Title")}
                  fieldName="title"
                  placeholder={t('enter_name', { key: lowerFirst(generalOptionState.type) })}
                  isRequired={true}
                />
                <FormInput
                  tagTitle={t("Subtitle")}
                  fieldName="subtitle"
                  isRequired={true}
                  placeholder={t("Ex: Wednesday 2:00 - 3:00 PM")}
                />
                <div className={styles.mt_16}>
                  <Label
                    text={t("Privacy")}
                    required={true}
                    item={
                      // tslint:disable-next-line:jsx-wrap-multiline
                      <ButtonsContainer
                        customStyle={styles.pricing_item}
                        items={privacy_options}
                        containerStyle={styles.option_button_group}
                        openCalendarSelected={privacy}
                        setOpenCalendarSelected={setPrivacy}
                      />
                    }
                    containerStyle={styles.label_container}
                  />
                </div>
                <div className={styles.mt_16}>
                  <Label
                    text={t("Can Registrants Sign Up Others From My Contacts?")}
                    required={true}
                    tooltip={(
                      <ToolTip
                        text={t(
                          "When set to Yes, registrants can view and select athletes from your contact list during registration. Registrants can always add their own family members, regardless of this setting."
                        )}
                      />
                    )}
                    item={
                      <ButtonsContainer
                        customStyle={styles.pricing_item}
                        items={restriction_options}
                        containerStyle={styles.option_button_group}
                        openCalendarSelected={restriction}
                        setOpenCalendarSelected={setRestriction}
                      />
                    }
                    containerStyle={styles.label_container}
                  />
                </div>
              </div>
            </div>
            <div className={styles.vertical_line} />
            <div className={`${utilStyles.column} ${styles.middle_section}`}>
              <div className={`${utilStyles.column}  `}>
                <label>{t("Date & Time")}</label>
                <div className={styles.horizontal_line} />
                <div className={`${utilStyles.column} ${utilStyles.mt_16}`}>
                  <div>
                    <Label
                      text={t("Date")}
                      required={true}
                      item={calendar}
                      error={formErrors.date ? t("This field is required") : ""}
                    />
                  </div>
                  <div
                    className={`${styles.time_container} ${utilStyles.mt_16}`}
                  >
                    <Label
                      text={t("Start Time")}
                      item={(
                        <SelectBox
                          options={hoursWithLablesFromSpecificValue(
                            endTime,
                            "end",
                            schedulingInterval,
                            timeFormat,
                          )}
                          customColor="#e6e6e6"
                          value={startTime}
                          setValue={(time) => onChange(time, "startTime")}
                          containerStyle={styles.mt_8}
                        />
                      )}
                      showUnder
                      containerStyle={styles.label_container}
                      error={
                        formErrors.startTime ? t("This field is required") : ""
                      }
                      required={true}
                    />
                    <Label
                      text={t("End Time")}
                      item={(
                        <SelectBox
                          options={hoursWithLablesFromSpecificValue(
                            startTime,
                            "start",
                            schedulingInterval,
                            timeFormat,
                          )}
                          customColor="#e6e6e6"
                          value={endTime}
                          setValue={(time) => onChange(time, "endTime")}
                          containerStyle={styles.mt_8}
                        />
                      )}
                      showUnder
                      containerStyle={styles.label_container}
                      error={
                        formErrors.endTime ? t("This field is required") : ""
                      }
                      required={true}
                    />
                  </div>
                  <div className={`${utilStyles.mt_16}`}>
                    <Label
                      text={t("Repeat")}
                      item={(
                        <SelectBox
                          isDisabled={generalOptionState.edit}
                          customColor="#e6e6e6"
                          options={repeat_options(generalOptionState?.type === 'Clinic')}
                          value={repeat}
                          setValue={handleRepeat}
                        />
                      )}
                      containerStyle={styles.label_container}
                      error={
                        formErrors.repeat ? t("This field is required") : ""
                      }
                      required={true}
                    />
                    {repeat?.value === "custom" && customRecurrenceConfig && (
                      <div className={styles.recurrence_summary}>
                        {buildCustomRecurrenceString(customRecurrenceConfig)}
                      </div>
                    )}
                    {repeat && repeat.value !== "Never" && repeat.value !== "custom" && (
                      <>
                        <Label
                          containerStyle={utilStyles.mt_16}
                          text={t("Until Date")}
                          required={true}
                          item={(
                            <div className={utilStyles.mt_8}>
                              <StateDayPicker
                                disabled={generalOptionState.edit}
                                onChange={(value) =>
                                  handleDateChange(value, "open")
                                }
                                maxDate={maxDate}
                                date={untilDate ? new Date(untilDate) : undefined}
                                openToDate={
                                  untilDate === null ? new Date() : null
                                }
                              />
                            </div>
                          )}
                        />
                      </>
                    )}
                    {generalOptionState?.type === 'Clinic' && repeat && repeat.value !== "Never" && (
                      <Label
                        containerStyle={utilStyles.mt_16}
                        text={t("Display")}
                        required={true}
                        item={(
                          <ButtonsContainer
                            customStyle={styles.pricing_item}
                            items={display_clinic_buttons()}
                            containerStyle={styles.option_button_group}
                            openCalendarSelected={selectedDisplayButton}
                            setOpenCalendarSelected={setSelectedDisplayButton}
                          />
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.vertical_line} />
            <div className={`${utilStyles.column} ${styles.contact_section}`}>
              <label>{t("Contact Info")}</label>
              <div className={styles.horizontal_line} />
              <div className={utilStyles.column}>
                <FormInput
                  tagTitle={t("Location")}
                  fieldName="location"
                  isRequired={true}
                />
                <FormInput
                  tagTitle={t("Email")}
                  fieldName="email"
                  isRequired={true}
                />
                <FormInput
                  tagTitle={t("Phone Number")}
                  fieldName="phone"
                  isRequired={true}
                />
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
      {untilDateError && (
        <InformationModal
          topImage={CalendarX}
          handleAction={() => setUntilDateError(false)}
          title={t("Error")}
          subtitle={t("Until date cannot be set before the Start date.")}
          width="500px"
        />
      )}
      <CustomRecurrenceModal
        isOpen={isCustomRecurrenceModalOpen}
        onClose={() => setIsCustomRecurrenceModalOpen(false)}
        onSave={handleSaveCustomRecurrence}
        initialConfig={customRecurrenceConfig}
        startTime={date}
      />
      <SaveBar
        showCancel
        onSubmit={methods.handleSubmit(onSubmit)}
        onCancel={() => {
          history.goBack();
          dispatch(resetProgramSettings());
        }}
      />
    </div>
  );
};

export default GeneralProgramOptions;
